import { useEffect, useMemo, useState } from "react";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import Button, { ButtonType } from "../../../components/Button";
import Alert, { AlertType } from "../../../components/Alert";
import DataTable from "../../../components/DataTable";
import { objectsEqual } from "../../../utils/validation";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	roleLabel,
	showRoleDropdown,
	CompanyUserRole,
	AssignableRoleOptionsBase,
} from "../../../utils/companyUserRole";
import { formatDate } from "../../../utils/formatting";

const ProvisionListUsers = ({
	fatalError,
	users,
	onOriginalDataReady,
	onChangedData,
	onDiffsChange,
	onResendEmail,
	onAdminValidationChange,
	setModifiedUsers
}) => {
	const [originalRows, setOriginalRows] = useState([]);
	const [rows, setRows] = useState([]);

	// Quick lookup of originals by username for compare/highlight
	const originalByUsername = useMemo(() => {
		const m = new Map();
		originalRows.forEach((u) => m.set(u.Username, u));
		return m;
	}, [originalRows]);

	// helper: accent/case-insensitive key for last name sorting
	const normalizeForSort = (s) =>
		(s ?? "")
			.normalize("NFD")
			.replace(/[\u0300-\u036f]/g, "")
			.toLowerCase()
			.trim();

	// helper: decide whether to show resend button
	const shouldShowResend = (r) => !r.LastLoginDate && !!r.ProvisionedDate && !r.RegisteredElsewhereCompanyName;


	// Initialize original + working copies whenever users changes
	useEffect(() => {
		const withDerived = (users ?? []).map((u) => ({
			...u,
			__fullName: `${u.FirstName ?? ""} ${u.LastName ?? ""}`.replace(/\s+/g, " ").trim(),
			__lastNameSort: `${normalizeForSort(u.LastName)}|${normalizeForSort(u.FirstName)}`,
		}));

		setOriginalRows(withDerived.map((u) => ({ ...u })));

		// If already provisioned but undefined role, default to Customer Admin (4)
		const enforced = withDerived.map((u) => {
			const isUndefined = u.RoleId == null || u.RoleId === CompanyUserRole.Undefined;
			return u.Provisioned && isUndefined
				? { ...u, RoleId: CompanyUserRole.CustomerAdmin }
				: u;
		});

		setRows(enforced);
		onOriginalDataReady?.(withDerived);
	}, [users]);

	// data table handlers
	const handleToggleProvisioned = (username, checked) => {
		setRows(prev =>
			prev.map(r => {
				if (r.Username !== username) {
					return r;
				}
				if (r.RegisteredElsewhereCompanyName) {
					return r;
				}// <-- block changes

				const orig = originalByUsername.get(username);
				const origLevel = orig?.RoleId;

				if (!checked) {
					return {
						...r,
						Provisioned: false,
						RoleId: origLevel ?? CompanyUserRole.Undefined,
					};
				}
				const nextRole =
					origLevel == null || origLevel === CompanyUserRole.Undefined
						? CompanyUserRole.CustomerAdmin
						: origLevel;

				return { ...r, Provisioned: checked, RoleId: nextRole };
			})
		);
	};

	const handleChangeRole = (username, newLevel) => {
		setRows(prev =>
			prev.map(r => {
				if (r.Username !== username) {
					return r;
				}
				if (r.RegisteredElsewhereCompanyName) {
					return r;
				}// <-- block changes

				const isUndefined = newLevel == null || newLevel === CompanyUserRole.Undefined;
				const nextLevel = r.Provisioned && isUndefined
					? CompanyUserRole.CustomerAdmin
					: newLevel;
				return { ...r, RoleId: nextLevel };
			})
		);
	};

	// resend registration click
	const handleResendClick = (row) => {
		onResendEmail?.(row);
	};

	// diffs vs original (for parent callbacks and highlight)
	const diffs = useMemo(() => {
		return rows.reduce((acc, curr) => {
			const orig = originalByUsername.get(curr.Username);
			if (!orig) return acc;
			if (!objectsEqual(curr, orig)) {
				const changedFields = [];
				const keys = new Set([...Object.keys(curr), ...Object.keys(orig)]);
				keys.forEach((k) => {
					const a = curr[k];
					const b = orig[k];
					if (JSON.stringify(a) !== JSON.stringify(b)) changedFields.push(k);
				});
				acc.push({ username: curr.Username, original: orig, current: curr, changedFields });
			}
			return acc;
		}, []);
	}, [rows, originalByUsername]);

	// Log modified rows + their new data whenever changes occur (including empty)
	useEffect(() => {
		const summary = diffs.map(({ username, changedFields, current }) => ({
			username,
			changedFields,
			newData: current,
		}));
		setModifiedUsers(summary);
	}, [diffs]);

	// Push up arrays/diffs if parent wants them
	useEffect(() => {
		onChangedData?.(rows);
		onDiffsChange?.(diffs);
	}, [rows, diffs]); // eslint-disable-line react-hooks/exhaustive-deps

	// modified state (only checks the two editable fields)
	const isRowModified = (rowData) => {
		const orig = originalByUsername.get(rowData.Username);
		if (!orig) return false;
		const registeredChanged = !!rowData.Provisioned !== !!orig.Provisioned;
		const roleChanged = rowData.RoleId !== orig.RoleId;
		return registeredChanged || roleChanged;
	};

	// Validation: at least one Customer Admin in the applied state
	const hasAnyCustomerAdmin = useMemo(
		() => rows.some(r => r.Provisioned === true && r.RoleId === CompanyUserRole.CustomerAdmin),
		[rows]
	);

	useEffect(() => {
		onAdminValidationChange?.(hasAnyCustomerAdmin);
	}, [hasAnyCustomerAdmin, onAdminValidationChange]);

	// Row highlight
	const rowClassName = (rowData) => ({
		red: !!rowData.RegisteredElsewhereCompanyName,
		modified: isRowModified(rowData),
	});

	const columns = useMemo(
		() => [
			{
				field: "IsInCrm",
				headerName: "Is In CRM",
				class: "header-center",
				sortable: true,
				valueGetter: (r) => (r.IsInCrm ? <FontAwesomeIcon icon={faCheck} /> : null),
				style: { width: "120px", textAlign: "center" },
			},
			{
				field: "__lastNameSort",
				headerName: "Name",
				sortable: true,
				valueGetter: (r) => r.__fullName,
				style: { width: "30%" },
			},
			{
				field: "Username",
				headerName: "Email",
				style: { width: "30%" },
				sortable: true,
			},
			{
				headerName: "Provisioned",
				class: "header-center",
				valueGetter: (rowData) =>
					rowData.RegisteredElsewhereCompanyName ? (
						<span /> // blank
					) : (
						<Checkbox
							checked={!!rowData.Provisioned}
							onChange={(e) => handleToggleProvisioned(rowData.Username, e.checked)}
							disabled={!!rowData.RegisteredElsewhereCompanyName}
						/>
					),
				style: { width: "140px", textAlign: "center" },
				sortable: false,
			},
			{
				field: "RoleId",
				headerName: "Access Level",
				sortable: true,
				style: { width: "180px" },
				valueGetter: (rowData) => {
					if (rowData.RegisteredElsewhereCompanyName) {
						return <span />;
					}
					return showRoleDropdown(rowData.RoleId) ? (
						<Dropdown
							value={rowData.RoleId}
							options={
								rowData.Provisioned
									? AssignableRoleOptionsBase
									: [{ label: "-", value: CompanyUserRole.Undefined }, ...AssignableRoleOptionsBase]
							}
							onChange={(e) => handleChangeRole(rowData.Username, e.value)}
							optionLabel="label"
							optionValue="value"
							placeholder="Select role"
							style={{ width: "100%" }}
							disabled={!rowData.Provisioned}
							className={!rowData.Provisioned ? "dropdown-disabled" : undefined}
						/>
					) : (
						<span>{roleLabel(rowData.RoleId)}</span>
					);
				},
			},
			{
				field: "ProvisionedDate",
				headerName: "Provisioned Date",
				sortable: true,
				valueGetter: (r) =>
					r.RegisteredElsewhereCompanyName
						? r.RegisteredElsewhereCompanyName
						: formatDate(r.ProvisionedDate),
				style: { minWidth: "170px" },
			},
			{
				field: "LastLoginDate",
				headerName: "Last Login Date",
				sortable: true,
				valueGetter: (r) =>
					r.LastLoginDate
						? formatDate(r.LastLoginDate)
						: shouldShowResend(r) ? (
							<Button
								type={ButtonType.Primary}
								className="font_xs"
								label={"Resend Registration"}
								onClick={() => handleResendClick(r)}
							/>
						) : (
							"-"
						),
				style: { minWidth: "180px" },
			},
		],
		[handleToggleProvisioned, handleChangeRole, onResendEmail]
	);

	return (
		<>
			<h4 className="title-top-margin table-title">Users</h4>
			<p>A customer needs at least one selected user to be provisioned.</p>

			{fatalError && (
				<Alert type={AlertType.Error}>
					Error Fetching Users: Please try again. If the problem persists, please contact the development department.
				</Alert>
			)}

			{!fatalError && (
				<>
					<div className="provisioning-buttons">
						{!hasAnyCustomerAdmin && <Alert type={AlertType.Warning}>
							There is no Customer Admin selected. A company needs at least 1 customer admin
						</Alert>}
					</div>
					<DataTable
						columns={columns}
						rows={rows}
						rowsPerPage={5}
						responsiveLayout="scroll"
						rowClassName={rowClassName}
						globalFilterFields={["Username", "__lastNameSort"]}
					/>
				</>
			)}
		</>
	);
};

export default ProvisionListUsers;
